/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

/**
 * scrollTrigger-trigger.js
 *
 * 使用中のScrollTriggerアニメーション（リファクタリング版）
 * 保守性と可読性を向上させた最適化版
 *
 */

/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/

import gsap from "gsap";
import scrollTrigger from "gsap/ScrollTrigger";
import { CSSPlugin } from "gsap/CSSPlugin";

/**
 * 設定定数
 */
const CONFIG = {
	ANIMATION_SPEED: 1,
	DEBUG_MODE: process.env.NODE_ENV !== "production",
	BREAKPOINTS: {
		DESKTOP: "(min-width: 761px)",
		MOBILE: "(max-width: 760px)",
	},
	SELECTORS: {
		TRIGGER: ".trigger",
		FADEIN: ".fadein",
		FADEIN_TRAIN: ".fadein__train",
		SLIDE_UP_TRAIN: ".slide__up__train",
		ADD_FIXED: "#addFixed",
		ADD_FIXED_SP: "#addFixedSp",
		D_NAV_BTN: "#d-nav-btn",
		NAV_FIXED_TRIGGER: "#navFixedTrigger",
		NAV_FIXED: "#navFixed",
	},
	CLASSES: {
		BTN_FIXED: "btnFixed",
		TRANSFORM_NAV: "transformNav",
	},
};

/**
 * デバッグログ関数
 */
const debugLog = (message, data = null) => {
	if (CONFIG.DEBUG_MODE) {
		if (data) {
			console.log(`[ScrollTrigger] ${message}`, data);
		} else {
			console.log(`[ScrollTrigger] ${message}`);
		}
	}
};

/**
 * エラーハンドリング関数
 */
const handleError = (error, context) => {
	console.error(`[ScrollTrigger Error] ${context}:`, error);
};

/**
 * GSAPとScrollTriggerの初期化
 */
const initializeGSAP = () => {
	try {
		gsap.registerPlugin(scrollTrigger, CSSPlugin);
		debugLog("GSAPとScrollTriggerが正常に初期化されました");
		return true;
	} catch (error) {
		handleError(error, "GSAP初期化");
		return false;
	}
};

// 初期化実行
const isGSAPInitialized = initializeGSAP();

/**
 * 要素の取得と検証
 */
const getElements = () => {
	const elements = {
		triggers: document.querySelectorAll(CONFIG.SELECTORS.TRIGGER),
		fadeins: document.querySelectorAll(CONFIG.SELECTORS.FADEIN),
		fadeinTrains: document.querySelectorAll(CONFIG.SELECTORS.FADEIN_TRAIN),
		slideUpTrains: document.querySelectorAll(CONFIG.SELECTORS.SLIDE_UP_TRAIN),
		addFixed: document.querySelector(CONFIG.SELECTORS.ADD_FIXED),
		addFixedSp: document.querySelector(CONFIG.SELECTORS.ADD_FIXED_SP),
		dNavBtn: document.querySelector(CONFIG.SELECTORS.D_NAV_BTN),
		navFixedTrigger: document.querySelector(CONFIG.SELECTORS.NAV_FIXED_TRIGGER),
		navFixed: document.querySelector(CONFIG.SELECTORS.NAV_FIXED),
		dNav: document.querySelector(CONFIG.SELECTORS.D_NAV),
		body: document.body,
	};

	debugLog("要素の取得結果", {
		triggers: elements.triggers.length,
		fadeins: elements.fadeins.length,
		fadeinTrains: elements.fadeinTrains.length,
		slideUpTrains: elements.slideUpTrains.length,
		addFixed: !!elements.addFixed,
		addFixedSp: !!elements.addFixedSp,
		dNavBtn: !!elements.dNavBtn,
		navFixedTrigger: !!elements.navFixedTrigger,
		navFixed: !!elements.navFixed,
	});

	return elements;
};

/**
 * 要素に最小高さを設定
 */
const setMinimumHeight = (element, elementName) => {
	if (element && element.offsetHeight === 0) {
		element.style.height = "1px";
		debugLog(`${elementName}要素に最小高さを設定`);
	}
};

/**
 * フェードインアニメーションの設定
 */
const setupFadeInAnimation = (elements, trigger) => {
	if (elements.length === 0) return;

	gsap.to(elements, {
		opacity: 1,
		duration: CONFIG.ANIMATION_SPEED,
		delay: 0.5,
		scrollTrigger: {
			trigger: trigger,
			start: "top 75%",
			toggleActions: "play none none reverse",
		},
	});
};

/**
 * トレイン型フェードインアニメーションの設定
 */
const setupFadeInTrainAnimation = (elements, trigger) => {
	if (elements.length === 0) return;

	gsap.to(elements, {
		opacity: 1,
		duration: CONFIG.ANIMATION_SPEED,
		delay: 0.5,
		scrollTrigger: {
			trigger: trigger,
			start: "top 70%",
			toggleActions: "play none none reverse",
		},
		stagger: {
			from: "start",
			amount: 0.7,
		},
	});
};

/**
 * スライドアップアニメーションの設定
 */
const setupSlideUpAnimation = (elements, trigger) => {
	if (elements.length === 0) return;

	gsap.to(elements, {
		opacity: 1,
		yPercent: 0,
		duration: CONFIG.ANIMATION_SPEED,
		scrollTrigger: {
			trigger: trigger,
			start: "top 75%",
			toggleActions: "play none none none",
		},
		stagger: {
			from: "start",
			amount: 0.7,
		},
	});
};

/**
 * 初期状態の設定
 */
const setInitialStates = (elements) => {
	if (elements.fadeins.length > 0) {
		gsap.set(elements.fadeins, { opacity: 0 });
	}
	if (elements.fadeinTrains.length > 0) {
		gsap.set(elements.fadeinTrains, { opacity: 0 });
	}
	if (elements.slideUpTrains.length > 0) {
		gsap.set(elements.slideUpTrains, { yPercent: 50, opacity: 0 });
	}
};

/**
 * アニメーションの設定
 */
const setupAnimations = (elements) => {
	for (let i = 0; i < elements.triggers.length; i++) {
		const trigger = elements.triggers[i];
		const fadeinElements = trigger.querySelectorAll(CONFIG.SELECTORS.FADEIN);
		const fadeinTrainElements = trigger.querySelectorAll(CONFIG.SELECTORS.FADEIN_TRAIN);
		const slideUpTrainElements = trigger.querySelectorAll(CONFIG.SELECTORS.SLIDE_UP_TRAIN);

		setupFadeInAnimation(fadeinElements, trigger);
		setupFadeInTrainAnimation(fadeinTrainElements, trigger);
		setupSlideUpAnimation(slideUpTrainElements, trigger);
	}
};

/**
 * ナビゲーションボタンの固定処理
 */
const setupNavigationButtonFixed = (elements) => {
	if (!elements.dNavBtn) {
		debugLog("d-nav-btn要素が見つかりません");
		return;
	}

	// PC版のScrollTrigger設定
	if (elements.addFixed) {
		debugLog("addFixed用のScrollTriggerを作成");
		setMinimumHeight(elements.addFixed, "addFixed");

		const trigger = scrollTrigger.create({
			trigger: elements.addFixed,
			start: "top top",
			onEnter: () => {
				debugLog("addFixed: onEnter実行 - btnFixedクラスを追加");
				elements.dNavBtn.classList.add(CONFIG.CLASSES.BTN_FIXED);
				debugLog("DNavBtnクラス", elements.dNavBtn.className);
			},
			onLeaveBack: () => {
				debugLog("addFixed: onLeaveBack実行 - btnFixedクラスを削除");
				elements.dNavBtn.classList.remove(CONFIG.CLASSES.BTN_FIXED);
				debugLog("DNavBtnクラス", elements.dNavBtn.className);
				// openクラスが付与されていれば削除
				if (document.body.classList.contains("open")) {
					document.body.classList.remove("open");
				}
				const dNav = document.querySelector("#site-h_d-nav");
				if (dNav && dNav.classList.contains("open")) {
					dNav.classList.remove("open");
				}
			},
			onUpdate: (self) => {
				debugLog("addFixed: onUpdate実行", {
					progress: self.progress,
					direction: self.direction,
					isActive: self.isActive,
				});
			},
		});

		debugLog("作成されたScrollTrigger", trigger);
	} else {
		debugLog("addFixed要素が見つかりません");
	}

	// モバイル版のScrollTrigger設定
	if (elements.addFixedSp) {
		debugLog("addFixedSp用のScrollTriggerを作成");
		setMinimumHeight(elements.addFixedSp, "addFixedSp");

		scrollTrigger.create({
			trigger: elements.addFixedSp,
			start: "top top",
			onEnter: () => {
				debugLog("addFixedSp: onEnter実行");
				elements.dNavBtn.classList.add(CONFIG.CLASSES.BTN_FIXED);
			},
			onLeaveBack: () => {
				debugLog("addFixedSp: onLeaveBack実行");
				elements.dNavBtn.classList.remove(CONFIG.CLASSES.BTN_FIXED);
			},
		});
	} else {
		debugLog("addFixedSp要素が見つかりません");
	}
};

/**
 * ナビゲーション固定処理
 */
const setupNavigationFixed = (elements) => {
	if (!elements.navFixedTrigger || !elements.navFixed) {
		debugLog("navFixedTriggerまたはnavFixed要素が見つかりません");
		return;
	}

	// レスポンシブ対応のナビゲーション固定処理
	scrollTrigger.matchMedia({
		[CONFIG.BREAKPOINTS.DESKTOP]: function () {
			debugLog("PC版のナビゲーション固定処理を実行");
			scrollTrigger.create({
				trigger: elements.navFixedTrigger,
				start: "top top",
				onEnter: () => elements.navFixed.classList.add(CONFIG.CLASSES.TRANSFORM_NAV),
				onLeaveBack: () => {
					elements.navFixed.classList.remove(CONFIG.CLASSES.TRANSFORM_NAV);
					// openクラスが付与されていれば削除
					if (document.body.classList.contains("open")) {
						document.body.classList.remove("open");
					}
					const dNav = document.querySelector("#site-h_d-nav");
					if (dNav && dNav.classList.contains("open")) {
						dNav.classList.remove("open");
					}
				},
			});
		},
		[CONFIG.BREAKPOINTS.MOBILE]: function () {
			debugLog("モバイル版のナビゲーション固定処理を実行");
			scrollTrigger.create({
				trigger: elements.navFixedTrigger,
				start: "top top",
				onEnter: () => elements.navFixed.classList.add(CONFIG.CLASSES.TRANSFORM_NAV),
				onLeaveBack: () => {
					elements.navFixed.classList.remove(CONFIG.CLASSES.TRANSFORM_NAV);
				},
			});
		},
	});
};

/**
 * メインのsetTrigger関数
 */
const setTrigger = () => {
	debugLog("setTrigger関数が実行されました");

	// GSAPの初期化確認
	if (!isGSAPInitialized) {
		handleError(new Error("GSAPが初期化されていません"), "setTrigger");
		return;
	}

	// ScrollTriggerの利用可能性を確認
	if (typeof scrollTrigger === "undefined") {
		handleError(new Error("ScrollTriggerが利用できません"), "setTrigger");
		return;
	}

	// 要素の取得
	const elements = getElements();

	// 初期状態の設定
	setInitialStates(elements);

	// アニメーションの設定
	setupAnimations(elements);

	// ナビゲーションボタンの固定処理
	setupNavigationButtonFixed(elements);

	// ナビゲーション固定処理
	setupNavigationFixed(elements);
};

/**
 * Export.
 */
/*━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━*/
export { setTrigger };
